/**
 * Live meekijken: één verbinding met de live-stroom voor het hele scherm.
 *
 * Contract: `docs/DESIGN-live-stroom.md` (sessie 52). Er komt geen tweede
 * stroom: de lijst, het gesprek en de kanaaltegels lezen alle drie uit
 * deze ene provider.
 *
 * Herstel (§6 van het contract, de eenvoudige variant): eerst `nu-bezig`,
 * dan de stroom openen, en na `: verbonden` nog één keer `nu-bezig`.
 * Geen event of ping binnen 45 seconden: de lijn is dood, opnieuw
 * verbinden met oplopende wachttijd (1, 2, 5, 10, 20, 30 s; daarna steeds
 * 30 s). Een tab die terugkomt van de achtergrond haalt eerst `nu-bezig` op.
 *
 * Sessie 85: bij een deploy stuurt de server een laatste `: afsluiten` en
 * sluit de stroom. Dan verbindt het scherm na 1 s opnieuw (naar de nieuwe
 * instantie), zonder op de waakhond te wachten. Een tweede `: afsluiten`
 * binnen 10 s na die herverbinding telt als mislukte poging.
 *
 * `fetch` in plaats van `EventSource`: die laatste kan geen
 * Authorization-header meesturen.
 *
 * Werkplek live (28-10): een tab die terugkomt (zichtbaar, of uit de
 * bfcache via `pageshow`) verbindt meteen opnieuw, ook als de lus net op
 * een wachttijd stond. Elke `: verbonden` telt op in `verbindingen`, zodat
 * het scherm na elke verbinding (ook de eerste) zaak en lijst bijtrekt.
 * Valt de stroom onverwacht weg (fout, server sluit, waakhond), dan meldt
 * het scherm dat na de herverbinding één keer aan de server
 * (`/api/werkplek/live/weg`, hooguit één keer per minuut). Afbreken door
 * de app zelf (tab terug, unmount) en een deploy (`: afsluiten`) tellen
 * niet; een stilte terwijl de tab verborgen was ook niet.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useInRouterContext, useNavigate } from "react-router-dom";

import { apiClient } from "@/lib/api";
import { huidigAccessToken } from "@/lib/auth-store";

import { leesSse, type Kanaal } from "./api";

export type LiveSoort =
  | "klantbericht"
  | "antwoord"
  | "concept"
  | "verzendstatus"
  | "typen"
  | "status";

export interface LiveEvent {
  soort: LiveSoort;
  kanaal: "chat" | "whatsapp" | "mail";
  gesprek_id: string;
  zaak_id: string | null;
  trede: string | null;
  bericht_id: string | null;
  rol: string | null;
  laatste_regel: string | null;
  verzendstatus: string | null;
  is_test: boolean;
  op: string;
  extra: Record<string, unknown>;
}

export interface NuBezigRij {
  kanaal: "chat" | "whatsapp" | "mail";
  gesprek_id: string;
  zaak_id: string | null;
  naam: string;
  trede: string;
  laatste_regel: string;
  laatste_rol: string;
  status: string;
  laatste_op: string;
  verzendstatus: string | null;
  is_test: boolean;
}

interface NuBezigAntwoord {
  minuten: number;
  sinds: string;
  rijen: NuBezigRij[];
}

/** Hoe lang een gesprek "live" heet na het laatste bericht. */
export const LIVE_VENSTER_MS = 5 * 60_000;
/** Het contract: "typt..." ongeveer 6 seconden na het laatste typen-event. */
const TYPEN_MS = 6_000;
/** Geen event of ping binnen deze tijd: de lijn is dood (ping elke 15 s). */
export const DOOD_NA_MS = 45_000;
/** Wachttijd voor de n-de herverbinding op rij; na de laatste blijft het 30 s. */
export const WACHTTIJDEN = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000] as const;

/**
 * Een tweede `: afsluiten` binnen deze tijd na de herverbinding telt als
 * mislukte poging (oplopende wachttijd) in plaats van weer na 1 s.
 */
export const AFSLUIT_VENSTER_MS = 10_000;

/** Werkplek live (28-10): hooguit één melding "stroom was weg" per minuut. */
export const WEG_MELDING_PER_MS = 60_000;
/** Recensie 28-10: zo lang weg zonder herverbinding = al melden (de stroom
 *  kan stuk zijn terwijl de rest van de API werkt, bijv. 401 of 500). */
export const WEG_LANG_MS = 60_000;

/** Werkplek live (28-10): waarom de stroom onverwacht wegviel. */
export type WegReden = "waakhond" | "fout" | "gesloten";

/** De wachttijd vóór herverbindpoging `poging` (0 = de eerste). */
export function wachttijd(poging: number): number {
  return WACHTTIJDEN[Math.min(Math.max(poging, 0), WACHTTIJDEN.length - 1)];
}

export interface LiveStand {
  verbonden: boolean;
  /**
   * Werkplek live (28-10): telt elke `: verbonden` op, ook de eerste. Een
   * nieuwe waarde betekent: de stroom is (weer) open, trek bij wat er
   * vóór die tijd geladen was.
   */
  verbindingen: number;
  /** Stand van nu per gesprek (chatsessie of e-mail). */
  rijen: Map<string, NuBezigRij>;
  /** Gesprekken waar de klant nu typt. */
  typen: Set<string>;
  /** Is Nova in dit gesprek live bezig (open, niet overgenomen, recent)? */
  isLive: (gesprekIds: string[] | string | null | undefined) => boolean;
  /** Kanalen waar Nova nu ergens live bezig is (voor de oranje stip). */
  liveKanalen: Set<Kanaal>;
  /** Typt de klant in een van deze gesprekken? */
  typt: (gesprekIds: string[] | string | null | undefined) => boolean;
  /** Schrijf je in op elk event; geeft een afmeldfunctie terug. */
  abonneer: (luisteraar: (event: LiveEvent) => void) => () => void;
}

const LiveContext = createContext<LiveStand | null>(null);

function alsLijst(ids: string[] | string | null | undefined): string[] {
  if (!ids) return [];
  return Array.isArray(ids) ? ids : [ids];
}

/** Rij is live: chat of WhatsApp, Nova aan zet, activiteit binnen het venster. */
export function rijIsLive(rij: NuBezigRij, nu: number = Date.now()): boolean {
  if (rij.kanaal === "mail") return false;
  if (rij.status !== "open") return false;
  return nu - new Date(rij.laatste_op).getTime() <= LIVE_VENSTER_MS;
}

export function LiveProvider({ children }: { children: ReactNode }) {
  const [rijen, setRijen] = useState<Map<string, NuBezigRij>>(() => new Map());
  const [typen, setTypen] = useState<Set<string>>(() => new Set());
  const [verbonden, setVerbonden] = useState(false);
  const [verbindingen, setVerbindingen] = useState(0);
  const inRouter = useInRouterContext();
  // Tik elke 30 s zodat "live" vanzelf verloopt zonder nieuw event.
  const [tik, setTik] = useState(0);
  const luisteraars = useRef(new Set<(e: LiveEvent) => void>());
  const typenTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const haalNuBezig = useCallback(async () => {
    try {
      const { data } = await apiClient.get<NuBezigAntwoord>(
        "/api/werkplek/live/nu-bezig",
        { params: { minuten: 30 } },
      );
      setRijen(new Map(data.rijen.map((r) => [r.gesprek_id, r])));
    } catch {
      /* stand blijft staan; de stroom herstelt het */
    }
  }, []);

  const verwerk = useCallback((event: LiveEvent) => {
    if (event.is_test) return;
    if (event.soort === "typen") {
      const id = event.gesprek_id;
      setTypen((oud) => new Set(oud).add(id));
      const oudeTimer = typenTimers.current.get(id);
      if (oudeTimer) clearTimeout(oudeTimer);
      typenTimers.current.set(
        id,
        setTimeout(() => {
          typenTimers.current.delete(id);
          setTypen((oud) => {
            const nieuw = new Set(oud);
            nieuw.delete(id);
            return nieuw;
          });
        }, TYPEN_MS),
      );
    } else {
      // Een echt bericht van de klant beëindigt het typen.
      if (event.soort === "klantbericht") {
        setTypen((oud) => {
          if (!oud.has(event.gesprek_id)) return oud;
          const nieuw = new Set(oud);
          nieuw.delete(event.gesprek_id);
          return nieuw;
        });
      }
      setRijen((oud) => {
        const nieuw = new Map(oud);
        const vorig = nieuw.get(event.gesprek_id);
        if (event.soort === "verzendstatus") {
          // Sessie 72: een verzendstatus (ook die van de wachtrij, na
          // Versturen door JP) maakt nooit een nieuwe rij en schuift het
          // live-venster niet op; anders staat de live-stip 5 minuten aan.
          // Een bestaande rij krijgt alleen de nieuwe status.
          if (!vorig) return oud;
          nieuw.set(event.gesprek_id, {
            ...vorig,
            verzendstatus: event.verzendstatus ?? vorig.verzendstatus,
          });
          return nieuw;
        }
        if (vorig && new Date(event.op) < new Date(vorig.laatste_op)) return oud;
        // De backend stuurt de stand als `status` (afgerond, overgenomen,
        // heropend); `stand` blijft gelezen voor oudere events (sessie 70).
        const uitEvent = event.soort === "status" ? (event.extra?.status ?? event.extra?.stand) : undefined;
        const stand = typeof uitEvent === "string" ? uitEvent : vorig?.status ?? "open";
        nieuw.set(event.gesprek_id, {
          kanaal: event.kanaal,
          gesprek_id: event.gesprek_id,
          zaak_id: event.zaak_id ?? vorig?.zaak_id ?? null,
          naam: vorig?.naam ?? "onbekend",
          trede: event.trede ?? vorig?.trede ?? "geen",
          laatste_regel: event.laatste_regel ?? vorig?.laatste_regel ?? "",
          laatste_rol: event.rol ?? vorig?.laatste_rol ?? "klant",
          status: stand,
          laatste_op: event.op,
          verzendstatus: event.verzendstatus ?? vorig?.verzendstatus ?? null,
          is_test: false,
        });
        return nieuw;
      });
    }
    for (const luisteraar of luisteraars.current) {
      try {
        luisteraar(event);
      } catch {
        /* een luisteraar breekt de stroom nooit */
      }
    }
  }, []);

  useEffect(() => {
    let gestopt = false;
    let poging = 0;
    // `: afsluiten`-bewaking: wanneer de huidige stroom openging, of die
    // verbinding volgde op een `: afsluiten`, en hoeveel snelle op rij.
    let geopendOp = 0;
    let naAfsluiten = false;
    let afsluitReeks = 0;
    let controller: AbortController | null = null;
    let waakhond: ReturnType<typeof setTimeout> | null = null;
    let opnieuw: ReturnType<typeof setTimeout> | null = null;
    // Werkplek live (28-10): wie brak de lopende stroom af? `app` = het
    // scherm zelf (tab terug); dat is geen storing. `direct` = de volgende
    // poging zonder wachttijd (tab terug).
    let afgebrokenDoor: "waakhond" | "app" | null = null;
    let direct = false;
    // Bewaking: het laatste teken van leven van de stroom, wanneer de tab
    // voor het laatst verborgen raakte, en de wegval die nog gemeld moet.
    let laatsteTeken = Date.now();
    let verborgenOp = document.visibilityState === "hidden" ? Date.now() : 0;
    let weg: { reden: WegReden; sinds: number; gemeld: boolean } | null = null;
    let laatsteMelding = 0;
    let langWeg: ReturnType<typeof setTimeout> | null = null;

    const stuurWeg = (reden: WegReden, wegMs: number) => {
      try {
        void apiClient
          .post("/api/werkplek/live/weg", { reden, weg_ms: Math.max(0, Math.round(wegMs)) })
          .catch(() => undefined);
      } catch {
        /* melden is nooit belangrijker dan meekijken */
      }
    };

    const wek = () => {
      laatsteTeken = Date.now();
      if (waakhond) clearTimeout(waakhond);
      waakhond = setTimeout(() => {
        afgebrokenDoor = "waakhond";
        controller?.abort();
      }, DOOD_NA_MS);
    };

    /** Werkplek live (28-10): onthoud een onverwachte wegval (de eerste telt). */
    const onthoudWeg = (reden: WegReden) => {
      if (weg) return;
      // Verborgen tab: iOS bevriest de lijn en de timers; die stilte is
      // geen storing.
      if (document.visibilityState === "hidden" || verborgenOp >= laatsteTeken) return;
      weg = { reden, sinds: reden === "waakhond" ? laatsteTeken : Date.now(), gemeld: false };
      // Blijft de stroom weg, dan niet wachten op een herverbinding die
      // misschien nooit komt: na WEG_LANG_MS al melden (één keer).
      if (langWeg) clearTimeout(langWeg);
      langWeg = setTimeout(() => {
        langWeg = null;
        if (!weg || weg.gemeld || document.visibilityState === "hidden") return;
        weg.gemeld = true;
        laatsteMelding = Date.now();
        stuurWeg(weg.reden, Date.now() - weg.sinds);
      }, WEG_LANG_MS);
    };

    /** Werkplek live (28-10): de stroom is terug; meld een wegval één keer. */
    const meldWeg = () => {
      const was = weg;
      weg = null;
      if (langWeg) clearTimeout(langWeg);
      langWeg = null;
      if (!was || was.gemeld) return;
      const nu = Date.now();
      if (laatsteMelding && nu - laatsteMelding < WEG_MELDING_PER_MS) return;
      laatsteMelding = nu;
      stuurWeg(was.reden, nu - was.sinds);
    };

    const verbind = async () => {
      if (gestopt) return;
      opnieuw = null;
      let serverSloot = false;
      // Eerst nu-bezig via apiClient: dat ververst ook een verlopen token.
      await haalNuBezig();
      if (gestopt) return;
      controller = new AbortController();
      afgebrokenDoor = null;
      const token = huidigAccessToken();
      let reden: WegReden | null;
      try {
        const antwoord = await fetch("/api/werkplek/live/stroom", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
          cache: "no-store",
        });
        if (!antwoord.ok || !antwoord.body) throw new Error(`stroom ${antwoord.status}`);
        geopendOp = Date.now();
        wek();
        await leesSse(
          antwoord.body,
          (data) => {
            wek();
            poging = 0;
            verwerk(data as unknown as LiveEvent);
          },
          (commentaar) => {
            wek();
            if (commentaar === "verbonden") {
              poging = 0;
              setVerbonden(true);
              setVerbindingen((n) => n + 1);
              void haalNuBezig();
              meldWeg();
            } else if (commentaar === "afsluiten") {
              serverSloot = true;
            }
          },
        );
        reden = "gesloten";
      } catch {
        /* afgebroken of mislukt: hieronder opnieuw */
        reden = "fout";
      }
      if (afgebrokenDoor === "waakhond") reden = "waakhond";
      else if (afgebrokenDoor === "app") reden = null;
      controller = null;
      if (waakhond) clearTimeout(waakhond);
      setVerbonden(false);
      if (gestopt) return;
      // Een deploy (`: afsluiten`) is aangekondigd: geen storing.
      if (reden && !serverSloot) onthoudWeg(reden);
      if (serverSloot) {
        // De server sloot zelf af (deploy). De eerste keer: backoff naar 0,
        // dus opnieuw na 1 s. Komt er binnen 10 s na die nieuwe verbinding
        // weer een `: afsluiten`, dan telt dat als mislukte poging en loopt
        // de wachttijd op, zodat een haperende server niet elke seconde
        // een nieuwe stroom krijgt.
        const snel = naAfsluiten && Date.now() - geopendOp < AFSLUIT_VENSTER_MS;
        afsluitReeks = snel ? afsluitReeks + 1 : 0;
        poging = snel ? Math.max(poging, afsluitReeks) : 0;
        naAfsluiten = true;
      } else {
        naAfsluiten = false;
        afsluitReeks = 0;
      }
      const wacht = direct ? 0 : wachttijd(poging);
      direct = false;
      poging += 1;
      opnieuw = setTimeout(() => void verbind(), wacht);
    };

    void verbind();

    const terug = () => {
      // iOS bevriest een verbinding op de achtergrond zonder hem te sluiten.
      // Bij terugkomst: afbreken, dan verbindt de lus meteen opnieuw (en
      // haalt eerst nu-bezig op), in plaats van tot 45 s op de waakhond te
      // wachten. Werkplek live (28-10): stond de lus op een wachttijd, dan
      // niet die wachttijd uitzitten maar nu verbinden.
      poging = 0;
      if (controller) {
        afgebrokenDoor = "app";
        direct = true;
        controller.abort();
      } else if (opnieuw) {
        clearTimeout(opnieuw);
        opnieuw = null;
        void verbind();
      } else {
        void haalNuBezig();
      }
    };
    const zichtbaar = () => {
      if (document.visibilityState === "visible") terug();
      else verborgenOp = Date.now();
    };
    // Werkplek live (28-10): uit de bfcache terug (iOS, terugknop) vuurt
    // geen visibilitychange, wel `pageshow` met `persisted`.
    const paginaTerug = (event: PageTransitionEvent) => {
      if (event.persisted) terug();
    };
    const paginaWeg = () => {
      verborgenOp = Date.now();
    };
    document.addEventListener("visibilitychange", zichtbaar);
    window.addEventListener("pageshow", paginaTerug);
    window.addEventListener("pagehide", paginaWeg);
    const klok = setInterval(() => setTik((t) => t + 1), 30_000);

    return () => {
      gestopt = true;
      controller?.abort();
      if (waakhond) clearTimeout(waakhond);
      if (opnieuw) clearTimeout(opnieuw);
      if (langWeg) clearTimeout(langWeg);
      clearInterval(klok);
      document.removeEventListener("visibilitychange", zichtbaar);
      window.removeEventListener("pageshow", paginaTerug);
      window.removeEventListener("pagehide", paginaWeg);
      for (const timer of typenTimers.current.values()) clearTimeout(timer);
      typenTimers.current.clear();
    };
  }, [haalNuBezig, verwerk]);

  const abonneer = useCallback((luisteraar: (event: LiveEvent) => void) => {
    luisteraars.current.add(luisteraar);
    return () => {
      luisteraars.current.delete(luisteraar);
    };
  }, []);

  const stand = useMemo<LiveStand>(() => {
    const nu = Date.now();
    const liveIds = new Set<string>();
    const liveKanalen = new Set<Kanaal>();
    for (const rij of rijen.values()) {
      if (rijIsLive(rij, nu)) {
        liveIds.add(rij.gesprek_id);
        liveKanalen.add(rij.kanaal as Kanaal);
      }
    }
    return {
      verbonden,
      verbindingen,
      rijen,
      typen,
      liveKanalen,
      isLive: (ids) => alsLijst(ids).some((id) => liveIds.has(id)),
      typt: (ids) => alsLijst(ids).some((id) => typen.has(id)),
      abonneer,
    };
    // `tik` laat "live" verlopen zonder nieuw event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rijen, typen, verbonden, verbindingen, abonneer, tik]);

  return (
    <LiveContext.Provider value={stand}>
      {inRouter ? <OpenVanuitMelding /> : null}
      {children}
    </LiveContext.Provider>
  );
}

/**
 * Werkplek live (28-10): een url uit een `vo-open`-bericht van de service
 * worker, als pad binnen dit portaal. Alleen dezelfde origin en een pad
 * dat met "/" begint; al het andere geeft null.
 */
export function eigenPad(url: unknown): string | null {
  if (typeof url !== "string" || url === "") return null;
  try {
    const doel = new URL(url, window.location.origin);
    if (doel.origin !== window.location.origin) return null;
    if (!doel.pathname.startsWith("/")) return null;
    return `${doel.pathname}${doel.search}${doel.hash}`;
  } catch {
    return null;
  }
}

/**
 * Werkplek live (28-10): een tik op een pushmelding. Kan de service worker
 * het venster niet zelf naar het gesprek sturen (iOS-PWA: `navigate`
 * ontbreekt of faalt), dan stuurt hij `{type: "vo-open", url}` en navigeert
 * de router hier, zonder de pagina te herladen.
 */
export function useOpenVanuitMelding(): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const sw = navigator.serviceWorker;
    if (!sw || typeof sw.addEventListener !== "function") return;
    const bijBericht = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; url?: unknown } | null;
      if (!data || data.type !== "vo-open") return;
      const pad = eigenPad(data.url);
      if (pad) navigate(pad);
    };
    sw.addEventListener("message", bijBericht);
    return () => sw.removeEventListener("message", bijBericht);
  }, [navigate]);
}

/** Werkplek live (28-10): de luisteraar hierboven, alleen binnen de router. */
function OpenVanuitMelding() {
  useOpenVanuitMelding();
  return null;
}

/** De live-stand. Buiten de provider: een lege, stille stand. */
export function useLive(): LiveStand {
  const stand = useContext(LiveContext);
  return stand ?? LEGE_STAND;
}

/**
 * Sessie 72: na een herverbinding één keer `bijtrekken` (de stroom was
 * dicht, er kan iets gemist zijn). Niet bij de eerste verbinding: dan is
 * alles net geladen.
 */
export function useNaHerverbinding(bijtrekken: () => void): void {
  const { verbonden } = useLive();
  const ref = useRef(bijtrekken);
  const ooitVerbonden = useRef(false);
  const vorige = useRef(verbonden);
  useEffect(() => {
    ref.current = bijtrekken;
  });
  useEffect(() => {
    if (verbonden && !vorige.current && ooitVerbonden.current) ref.current();
    if (verbonden) ooitVerbonden.current = true;
    vorige.current = verbonden;
  }, [verbonden]);
}

/** Roep `luisteraar` aan bij elk live-event zolang de component leeft. */
export function useLiveEvent(luisteraar: (event: LiveEvent) => void): void {
  const { abonneer } = useLive();
  const ref = useRef(luisteraar);
  useEffect(() => {
    ref.current = luisteraar;
  });
  useEffect(() => abonneer((e) => ref.current(e)), [abonneer]);
}

const LEGE_STAND: LiveStand = {
  verbonden: false,
  verbindingen: 0,
  rijen: new Map(),
  typen: new Set(),
  liveKanalen: new Set(),
  isLive: () => false,
  typt: () => false,
  abonneer: () => () => undefined,
};

/** Alleen voor tests: een provider met een vaste stand. */
export function LiveTestProvider({
  stand,
  children,
}: {
  stand: Partial<LiveStand>;
  children: ReactNode;
}) {
  return (
    <LiveContext.Provider value={{ ...LEGE_STAND, ...stand }}>{children}</LiveContext.Provider>
  );
}
